/**
 * The score's instrument voices as small native Web Audio graphs built per note (audio-direction §4.1): a note
 * creates a few oscillators and an envelope, schedules itself, and leaves the graph when it stops, so an idle
 * instrument costs the audio thread nothing (Tone.js's persistent voices cost far more; see D-049). Pure
 * builders: (context, destination, time, pitch, parameters) → scheduled nodes.
 */
import type { MusicConfig } from './musicSchema'

type Inst = MusicConfig['instruments']

/** A detuned saw stack with an attack–hold–release envelope (pads, brass swells, stingers). */
export function sawStack(ac: BaseAudioContext, dest: AudioNode, t: number, dur: number, hz: number, vel: number, saws: number, detuneCents: number, attack: number, release: number): void {
  const env = ac.createGain()
  env.gain.setValueAtTime(0, t)
  const peak = vel / Math.sqrt(saws)
  env.gain.linearRampToValueAtTime(peak, t + attack)
  env.gain.setValueAtTime(peak, t + Math.max(attack, dur))
  env.gain.setTargetAtTime(0, t + Math.max(attack, dur), release / 5)
  env.connect(dest)
  const end = t + Math.max(attack, dur) + release * 1.3
  for (let i = 0; i < saws; i++) {
    const o = ac.createOscillator()
    o.type = 'sawtooth'
    o.frequency.value = hz
    o.detune.value = saws > 1 ? ((i / (saws - 1)) * 2 - 1) * detuneCents : 0
    o.connect(env)
    o.start(t)
    o.stop(end)
  }
}

/** An additive organ pipe (one oscillator on a shared periodic wave). */
export function organNote(ac: BaseAudioContext, dest: AudioNode, t: number, dur: number, hz: number, vel: number, wave: PeriodicWave, cfg: Inst['organ']): void {
  const env = ac.createGain()
  env.gain.setValueAtTime(0, t)
  env.gain.linearRampToValueAtTime(vel, t + cfg.attack)
  env.gain.setValueAtTime(vel, t + Math.max(cfg.attack, dur))
  env.gain.setTargetAtTime(0, t + Math.max(cfg.attack, dur), cfg.release / 5)
  env.connect(dest)
  const o = ac.createOscillator()
  o.setPeriodicWave(wave)
  o.frequency.value = hz
  o.connect(env)
  o.start(t)
  o.stop(t + Math.max(cfg.attack, dur) + cfg.release * 1.3)
}

/** A warm FM pluck (the Spacer): the modulation index decays fast, the level slowly. */
export function fmPluck(ac: BaseAudioContext, dest: AudioNode, t: number, hz: number, vel: number, cfg: Inst['pluck']): void {
  const carrier = ac.createOscillator()
  carrier.frequency.value = hz
  const mod = ac.createOscillator()
  mod.frequency.value = hz * cfg.ratio
  const dev = ac.createGain()
  dev.gain.setValueAtTime(hz * cfg.index, t)
  dev.gain.setTargetAtTime(0, t, cfg.indexDecay / 4)
  mod.connect(dev)
  dev.connect(carrier.frequency)
  const env = ac.createGain()
  env.gain.setValueAtTime(0, t)
  env.gain.linearRampToValueAtTime(vel, t + 0.004)
  env.gain.setTargetAtTime(0, t + 0.004, cfg.decay / 5)
  carrier.connect(env)
  env.connect(dest)
  const end = t + cfg.decay * 1.4
  carrier.start(t)
  mod.start(t)
  carrier.stop(end)
  mod.stop(end)
}

/** One ostinato pulse: a filtered oscillator with a filter envelope (the Charter's pulse). */
export function ostPulse(ac: BaseAudioContext, dest: AudioNode, t: number, dur: number, hz: number, vel: number, cfg: Inst['ostA']): void {
  const o = ac.createOscillator()
  o.type = cfg.wave
  o.frequency.value = hz
  const f = ac.createBiquadFilter()
  f.type = 'lowpass'
  f.Q.value = cfg.q
  f.frequency.setValueAtTime(cfg.base * Math.pow(2, cfg.octaves), t)
  f.frequency.setTargetAtTime(cfg.base, t, cfg.filterDecay / 3)
  const env = ac.createGain()
  env.gain.setValueAtTime(0, t)
  env.gain.linearRampToValueAtTime(vel, t + 0.003)
  env.gain.setTargetAtTime(vel * cfg.sustain, t + 0.003, cfg.decay / 3)
  env.gain.setTargetAtTime(0, t + dur, cfg.release / 3)
  o.connect(f)
  f.connect(env)
  env.connect(dest)
  o.start(t)
  o.stop(t + dur + cfg.release * 2.5)
}

/** A pre-rendered hit (taiko, metal, snare) at a velocity and playback rate. */
export function hit(ac: BaseAudioContext, dest: AudioNode, t: number, buffer: AudioBuffer, vel: number, rate: number): void {
  const src = ac.createBufferSource()
  src.buffer = buffer
  src.playbackRate.value = rate
  const g = ac.createGain()
  g.gain.value = vel
  src.connect(g)
  g.connect(dest)
  src.start(t)
}

/** The organ's periodic wave from its drawbar partials. */
export function organWave(ac: BaseAudioContext, partials: readonly number[]): PeriodicWave {
  const real = new Float32Array(partials.length + 1)
  const imag = new Float32Array(partials.length + 1)
  for (let i = 0; i < partials.length; i++) imag[i + 1] = partials[i]
  return ac.createPeriodicWave(real, imag)
}
